<script setup>
import { Check, CircleHelp, Gauge, Lightbulb, ListOrdered } from 'lucide-vue-next';

// Renders one page guide entry (src/config/pageGuides.js).
//   full    – title + summary header, questions and steps side by side (EmptyProfile)
//   compact – single column, no title (the host supplies it, e.g. the header drawer)
// Entry copy is plain English rendered as-is (see the registry header for why);
// only the chrome below goes through $t.
defineProps({
  entry: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <section class="page-guide" :class="{ 'page-guide--compact': compact }">
    <header class="page-guide-header">
      <h3 v-if="!compact" class="page-guide-title">
        {{ entry.title }}
      </h3>
      <p class="page-guide-summary">
        {{ entry.summary }}
      </p>
      <el-tag
        v-if="entry.requires === 'ads'"
        class="page-guide-requires"
        size="small"
        round
        disable-transitions
      >
        {{ $t('Needs: Amazon Ads') }}
      </el-tag>
      <el-tag
        v-else-if="entry.requires === 'sp'"
        class="page-guide-requires"
        size="small"
        round
        disable-transitions
      >
        {{ $t('Needs: Seller Central') }}
      </el-tag>
    </header>

    <div class="page-guide-columns">
      <div v-if="entry.questions?.length" class="page-guide-block">
        <h4 class="page-guide-block-title">
          <CircleHelp :size="16" />
          {{ $t('Questions this page answers') }}
        </h4>
        <ul class="page-guide-list">
          <li v-for="question in entry.questions" :key="question" class="page-guide-list-item">
            <Check :size="14" class="page-guide-list-icon" />
            <span>{{ question }}</span>
          </li>
        </ul>
      </div>

      <div v-if="entry.howTo?.length" class="page-guide-block">
        <h4 class="page-guide-block-title">
          <ListOrdered :size="16" />
          {{ $t('How to use it') }}
        </h4>
        <ol class="page-guide-steps">
          <li v-for="(step, index) in entry.howTo" :key="index" class="page-guide-step">
            <span class="page-guide-step-number">{{ index + 1 }}</span>
            <span>{{ step }}</span>
          </li>
        </ol>
      </div>
    </div>

    <div v-if="entry.metrics?.length" class="page-guide-block">
      <h4 class="page-guide-block-title">
        <Gauge :size="16" />
        {{ $t('Key metrics') }}
      </h4>
      <dl class="page-guide-metrics">
        <div v-for="metric in entry.metrics" :key="metric.name" class="page-guide-metric">
          <dt class="page-guide-metric-name">
            {{ metric.name }}
          </dt>
          <dd class="page-guide-metric-description">
            {{ metric.description }}
          </dd>
        </div>
      </dl>
    </div>

    <div v-if="entry.tips?.length" class="page-guide-block page-guide-tips">
      <h4 class="page-guide-block-title">
        <Lightbulb :size="16" />
        {{ $t('Tips') }}
      </h4>
      <ul class="page-guide-list">
        <li v-for="tip in entry.tips" :key="tip" class="page-guide-list-item">
          <span>{{ tip }}</span>
        </li>
      </ul>
    </div>
  </section>
</template>

<style scoped lang="scss">
.page-guide {
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: var(--wp-text-primary);
  text-align: left;
}

.page-guide-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.page-guide-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--wp-text-primary);
}

.page-guide-summary {
  margin: 0;
  font-size: 15px;
  line-height: 22px;
  color: var(--wp-text-secondary);
}

.page-guide-requires {
  color: var(--wp-text-accent);
  background: var(--wp-surface-bg-icon);
  border-color: transparent;
}

.page-guide-columns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 20px;
}

.page-guide-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid var(--wp-border-primary);
  border-radius: 12px;
  background: var(--wp-surface-card);
}

.page-guide-block-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--wp-text-primary);
}

.page-guide-block-title svg {
  color: var(--wp-icon-accent);
}

.page-guide-list,
.page-guide-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.page-guide-list-item,
.page-guide-step {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
  color: var(--wp-text-secondary);
}

.page-guide-list-icon {
  flex: 0 0 auto;
  margin-top: 3px;
  color: var(--wp-text-success);
}

.page-guide-step-number {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
  color: var(--wp-text-accent);
  background: var(--wp-surface-bg-icon);
}

.page-guide-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
  gap: 12px;
  margin: 0;
}

.page-guide-metric-name {
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  color: var(--wp-text-primary);
}

.page-guide-metric-description {
  margin: 2px 0 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--wp-text-tertiary);
}

.page-guide--compact {
  gap: 16px;
}

.page-guide--compact .page-guide-columns,
.page-guide--compact .page-guide-metrics {
  grid-template-columns: 1fr;
  gap: 16px;
}

.page-guide--compact .page-guide-block {
  padding: 14px;
}
</style>
